import { useState, useEffect, Fragment } from 'react';
import supabase from '../supabaseClient';
import { Inp } from './ui/FormControls';
import { cargarReservas, reservaDeLote } from '../services/reservas';
import { terminarPedidoConDescuento } from '../services/pedidos-terminar';
import { exportarExcel, nombreArchivoExcel } from '../services/export-excel';

const ESTADO_STYLE = {
  pendiente: "bg-gray-100 text-gray-700",
  aceptado: "bg-blue-100 text-blue-700",
  en_proceso: "bg-amber-100 text-amber-700",
  terminado: "bg-green-100 text-green-700",
  cancelado: "bg-red-100 text-red-700",
};
const ESTADO_LABEL = {
  pendiente: "Pendiente",
  aceptado: "Aceptado",
  en_proceso: "En proceso",
  terminado: "Terminado",
  cancelado: "Cancelado",
};
const FILTROS_ESTADO = [
  { v: "", l: "Todos" },
  { v: "pendiente", l: "Pendiente" },
  { v: "aceptado", l: "Aceptado" },
  { v: "en_proceso", l: "En proceso" },
  { v: "terminado", l: "Terminado" },
  { v: "cancelado", l: "Cancelado" },
];
const ORDEN_ESTADO_TODOS = { en_proceso: 0, aceptado: 1, pendiente: 2, terminado: 3, cancelado: 4 };

const ESTADOS_EDITABLES = ["pendiente", "aceptado", "en_proceso"];
const hoyISO = () => new Date().toISOString().slice(0, 10);
const cabeceraVacia = () => ({ numeroPedido: "", cliente: "", fechaPedido: hoyISO(), fechaSolicitada: "", observaciones: "" });
const lineaVacia = () => ({ referencia: "", descripcion: "", lotes: [], loteId: "", fechaCaducidad: "", cajasDisponibles: 0, paletsDisponibles: 0, cajas: "", palets: "" });

const formatFecha = (iso) => iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("es-ES") : "—";

// ── OPERARIO (INTERNACIONAL): PREPARACIÓN DE PEDIDOS ──────────────
export const PedidosSection = ({ currentUser }) => {
  const almacen_id = currentUser?.almacen_id;
  const usuario = currentUser?.user;

  const [vista, setVista] = useState("lista");
  const [pedidos, setPedidos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [exito, setExito] = useState("");

  const [stockList, setStockList] = useState([]);
  const [cabecera, setCabecera] = useState(cabeceraVacia());
  const [lineas, setLineas] = useState([lineaVacia()]);
  const [confirmandoNuevo, setConfirmandoNuevo] = useState(false);
  const [guardandoNuevo, setGuardandoNuevo] = useState(false);

  const [reservasMap, setReservasMap] = useState(new Map());

  const [pedidoActivo, setPedidoActivo] = useState(null);
  const [terminando, setTerminando] = useState(false);
  const [njInput, setNjInput] = useState("");
  const [errorTerminar, setErrorTerminar] = useState("");
  const [cancelando, setCancelando] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [guardandoTransicion, setGuardandoTransicion] = useState(false);

  const [editandoCabecera, setEditandoCabecera] = useState(false);
  const [cabeceraEdit, setCabeceraEdit] = useState(cabeceraVacia());
  const [guardandoCabecera, setGuardandoCabecera] = useState(false);

  const [lineasExistentesEdit, setLineasExistentesEdit] = useState([]);
  const [lineasNuevasEdit, setLineasNuevasEdit] = useState([]);
  const [confirmandoLineas, setConfirmandoLineas] = useState(false);
  const [guardandoLineas, setGuardandoLineas] = useState(false);

  const [exportando, setExportando] = useState(false);

  const [pedidosSinLineas, setPedidosSinLineas] = useState(new Set());

  const cargarPedidos = async () => {
    if (!almacen_id) return;
    setCargando(true);
    const { data } = await supabase.from('pedidos').select('*').eq('almacen_id', almacen_id).order('creado_en', { ascending: false });
    setPedidos(data || []);

    const activosIds = (data || []).filter(p => ESTADOS_EDITABLES.includes(p.estado)).map(p => p.id);
    if (activosIds.length > 0) {
      const { data: lineasActivas } = await supabase.from('pedidos_lineas').select('pedido_id').in('pedido_id', activosIds);
      const conLineas = new Set((lineasActivas || []).map(l => l.pedido_id));
      setPedidosSinLineas(new Set(activosIds.filter(id => !conLineas.has(id))));
    } else {
      setPedidosSinLineas(new Set());
    }
    setCargando(false);
  };

  const cargarStock = async () => {
    if (!almacen_id) return [];
    const { data } = await supabase.from('stock').select('*').eq('almacen_id', almacen_id).order('referencia').order('fecha_caducidad');
    setStockList(data || []);
    return data || [];
  };

  useEffect(() => { cargarPedidos(); }, [almacen_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const referenciasUnicas = [...new Map(stockList.map(s => [s.referencia, s])).values()];

  const pedidosFiltrados = pedidos
    .filter(p => {
      if (filtroEstado && p.estado !== filtroEstado) return false;
      const q = busqueda.trim().toLowerCase();
      if (!q) return true;
      return p.numero_pedido.toLowerCase().includes(q) || (p.cliente || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (filtroEstado) return 0; // ya viene ordenado por fecha desde cargarPedidos
      return (ORDEN_ESTADO_TODOS[a.estado] ?? 99) - (ORDEN_ESTADO_TODOS[b.estado] ?? 99);
    });

  const exportarPedidos = async () => {
    setExportando(true);
    try {
      const ids = pedidosFiltrados.map(p => p.id);
      const { data: lineasData } = ids.length > 0
        ? await supabase.from('pedidos_lineas').select('*').in('pedido_id', ids)
        : { data: [] };
      const lineasPorPedido = new Map();
      for (const l of (lineasData || [])) {
        if (!lineasPorPedido.has(l.pedido_id)) lineasPorPedido.set(l.pedido_id, []);
        lineasPorPedido.get(l.pedido_id).push(l);
      }
      const filasPedidos = pedidosFiltrados.map(p => ({
        "Nº pedido": p.numero_pedido,
        "Cliente": p.cliente || "",
        "Fecha pedido": formatFecha(p.fecha_pedido),
        "Fecha solicitada": formatFecha(p.fecha_solicitada),
        "Estado": ESTADO_LABEL[p.estado] || p.estado,
        "NJ": p.nj || "",
        "Terminado en": p.terminado_en ? new Date(p.terminado_en).toLocaleString("es-ES") : "",
      }));
      const filasLineas = [];
      for (const p of pedidosFiltrados) {
        for (const l of (lineasPorPedido.get(p.id) || [])) {
          filasLineas.push({
            "Nº pedido": p.numero_pedido,
            "Referencia": l.referencia,
            "Descripción": l.descripcion || "",
            "Caducidad": formatFecha(l.fecha_caducidad),
            "Cajas": l.cantidad_cajas ?? 0,
            "Palets": l.cantidad_palets ?? 0,
          });
        }
      }
      exportarExcel(nombreArchivoExcel("pedidos", currentUser?.almacen_nombre), [
        { nombre: "Pedidos", filas: filasPedidos },
        { nombre: "Líneas", filas: filasLineas },
      ]);
    } finally {
      setExportando(false);
    }
  };

  const abrirNuevoPedido = async () => {
    setCabecera(cabeceraVacia());
    setLineas([lineaVacia()]);
    const [, reservas] = await Promise.all([cargarStock(), cargarReservas(almacen_id)]);
    setReservasMap(reservas);
    setVista("nuevo");
  };

  const abrirDetalle = async (pedido) => {
    const { data } = await supabase.from('pedidos_lineas').select('*').eq('pedido_id', pedido.id);
    setPedidoActivo({ ...pedido, lineas: data || [] });
    setVista("detalle");
  };

  const abrirEditarCabecera = () => {
    setCabeceraEdit({
      numeroPedido: pedidoActivo.numero_pedido || "",
      cliente: pedidoActivo.cliente || "",
      fechaPedido: pedidoActivo.fecha_pedido || hoyISO(),
      fechaSolicitada: pedidoActivo.fecha_solicitada || "",
      observaciones: pedidoActivo.observaciones || "",
    });
    setEditandoCabecera(true);
  };

  const guardarCabecera = async () => {
    if (!cabeceraEdit.numeroPedido.trim()) return;
    setGuardandoCabecera(true);
    try {
      const payload = {
        numero_pedido: cabeceraEdit.numeroPedido.trim(),
        cliente: cabeceraEdit.cliente.trim() || null,
        fecha_pedido: cabeceraEdit.fechaPedido,
        fecha_solicitada: cabeceraEdit.fechaSolicitada || null,
        observaciones: cabeceraEdit.observaciones.trim() || null,
      };
      await supabase.from('pedidos').update(payload).eq('id', pedidoActivo.id);
      setPedidoActivo(p => ({ ...p, ...payload }));
      setEditandoCabecera(false);
      await cargarPedidos();
    } finally {
      setGuardandoCabecera(false);
    }
  };

  const abrirEditarLineas = async () => {
    const [stock, reservas] = await Promise.all([cargarStock(), cargarReservas(almacen_id, pedidoActivo.id)]);
    setReservasMap(reservas);
    const existentes = pedidoActivo.lineas.map(l => {
      const lote = stock.find(s => s.referencia === l.referencia && (l.fecha_caducidad ? s.fecha_caducidad === l.fecha_caducidad : !s.fecha_caducidad));
      const reserva = reservaDeLote(reservas, l.referencia, l.fecha_caducidad);
      return {
        id: l.id,
        referencia: l.referencia,
        descripcion: l.descripcion,
        fecha_caducidad: l.fecha_caducidad,
        cantidadCajasOriginal: l.cantidad_cajas,
        cantidadPaletsOriginal: l.cantidad_palets,
        cajas: String(l.cantidad_cajas),
        palets: String(l.cantidad_palets),
        disponibleCajas: Math.max(0, (lote?.cantidad_cajas || 0) - reserva.cajas),
        disponiblePalets: Math.max(0, (lote?.cantidad_palets || 0) - reserva.palets),
        eliminada: false,
      };
    });
    setLineasExistentesEdit(existentes);
    setLineasNuevasEdit([]);
    setVista("editar-lineas");
  };

  const actualizarLineaExistente = (idx, campo, valor) => setLineasExistentesEdit(ls => ls.map((l, i) => i !== idx ? l : { ...l, [campo]: valor }));
  const toggleEliminarExistente = (idx) => setLineasExistentesEdit(ls => ls.map((l, i) => i !== idx ? l : { ...l, eliminada: !l.eliminada }));

  const lotesConDisponible = (referencia) => stockList.filter(s => s.referencia === referencia).map(s => {
    const reserva = reservaDeLote(reservasMap, s.referencia, s.fecha_caducidad);
    return { ...s, disponibleCajas: Math.max(0, (s.cantidad_cajas || 0) - reserva.cajas), disponiblePalets: Math.max(0, (s.cantidad_palets || 0) - reserva.palets) };
  });

  const actualizarLineaEdit = (idx, campo, valor) => setLineasNuevasEdit(ls => ls.map((l, i) => i !== idx ? l : { ...l, [campo]: valor }));
  const actualizarLineaReferenciaEdit = (idx, valor) => {
    const lotes = lotesConDisponible(valor);
    setLineasNuevasEdit(ls => ls.map((l, i) => i !== idx ? l : {
      ...l, referencia: valor, lotes, loteId: "", fechaCaducidad: "", cajasDisponibles: 0, paletsDisponibles: 0,
      descripcion: lotes[0]?.descripcion || "", cajas: "", palets: "",
    }));
  };
  const actualizarLineaLoteEdit = (idx, loteId) => {
    setLineasNuevasEdit(ls => ls.map((l, i) => {
      if (i !== idx) return l;
      const lote = l.lotes.find(s => s.id === loteId);
      return {
        ...l, loteId, fechaCaducidad: lote?.fecha_caducidad || "",
        cajasDisponibles: lote?.disponibleCajas || 0,
        paletsDisponibles: lote?.disponiblePalets || 0,
        descripcion: lote?.descripcion || l.descripcion, cajas: "", palets: "",
      };
    }));
  };
  const añadirLineaEdit = () => setLineasNuevasEdit(ls => [...ls, lineaVacia()]);
  const quitarLineaEdit = (idx) => setLineasNuevasEdit(ls => ls.filter((_, i) => i !== idx));

  const actualizarLinea = (idx, campo, valor) => setLineas(ls => ls.map((l, i) => i !== idx ? l : { ...l, [campo]: valor }));

  const actualizarLineaReferencia = (idx, valor) => {
    const lotes = lotesConDisponible(valor);
    setLineas(ls => ls.map((l, i) => i !== idx ? l : {
      ...l, referencia: valor, lotes, loteId: "", fechaCaducidad: "", cajasDisponibles: 0, paletsDisponibles: 0,
      descripcion: lotes[0]?.descripcion || "", cajas: "", palets: "",
    }));
  };

  const actualizarLineaLote = (idx, loteId) => {
    setLineas(ls => ls.map((l, i) => {
      if (i !== idx) return l;
      const lote = l.lotes.find(s => s.id === loteId);
      return {
        ...l, loteId, fechaCaducidad: lote?.fecha_caducidad || "",
        cajasDisponibles: lote?.disponibleCajas || 0,
        paletsDisponibles: lote?.disponiblePalets || 0,
        descripcion: lote?.descripcion || l.descripcion, cajas: "", palets: "",
      };
    }));
  };

  const añadirLinea = () => setLineas(ls => [...ls, lineaVacia()]);
  const quitarLinea = (idx) => setLineas(ls => ls.filter((_, i) => i !== idx));

  const lineaIniciada = l => l.referencia.trim() || Number(l.cajas) > 0 || Number(l.palets) > 0;
  const lineaCompleta = l => l.referencia.trim() && l.loteId && (Number(l.cajas) > 0 || Number(l.palets) > 0) && Number(l.cajas || 0) <= l.cajasDisponibles && Number(l.palets || 0) <= l.paletsDisponibles;
  const lineasValidas = lineas.filter(lineaCompleta);
  const hayLineaIncompleta = lineas.some(l => lineaIniciada(l) && !lineaCompleta(l));
  const formValido = cabecera.numeroPedido.trim() && !hayLineaIncompleta;

  const lineaExistenteSupera = (l) => (Number(l.cajas) || 0) > (l.disponibleCajas + l.cantidadCajasOriginal) || (Number(l.palets) || 0) > (l.disponiblePalets + l.cantidadPaletsOriginal);
  const lineaExistenteVacia = (l) => Number(l.cajas || 0) === 0 && Number(l.palets || 0) === 0;
  const hayLineaExistenteInvalida = lineasExistentesEdit.some(l => !l.eliminada && (lineaExistenteSupera(l) || lineaExistenteVacia(l)));
  const hayLineaNuevaIncompleta = lineasNuevasEdit.some(l => lineaIniciada(l) && !lineaCompleta(l));

  const cambiosParaConfirmar = () => {
    const cambios = [];
    for (const l of lineasExistentesEdit) {
      const nuevaCajas = l.eliminada ? 0 : (Number(l.cajas) || 0);
      const nuevaPalets = l.eliminada ? 0 : (Number(l.palets) || 0);
      const deltaCajas = l.cantidadCajasOriginal - nuevaCajas;
      const deltaPalets = l.cantidadPaletsOriginal - nuevaPalets;
      if (l.eliminada || deltaCajas !== 0 || deltaPalets !== 0) {
        cambios.push({ referencia: l.referencia, descripcion: l.descripcion, fecha_caducidad: l.fecha_caducidad, deltaCajas, deltaPalets, accion: l.eliminada ? "delete" : "update", lineaId: l.id, nuevaCajas, nuevaPalets });
      }
    }
    for (const l of lineasNuevasEdit.filter(lineaCompleta)) {
      const cajas = Number(l.cajas) || 0;
      const palets = Number(l.palets) || 0;
      cambios.push({ referencia: l.referencia.trim(), descripcion: l.descripcion.trim(), fecha_caducidad: l.fechaCaducidad, deltaCajas: -cajas, deltaPalets: -palets, accion: "insert", cajas, palets });
    }
    return cambios;
  };

  const resumenPorLote = () => {
    const map = new Map();
    for (const c of cambiosParaConfirmar()) {
      const key = `${c.referencia}|${c.fecha_caducidad || "null"}`;
      if (!map.has(key)) map.set(key, { referencia: c.referencia, fecha_caducidad: c.fecha_caducidad, deltaCajas: 0, deltaPalets: 0 });
      const e = map.get(key);
      e.deltaCajas += c.deltaCajas;
      e.deltaPalets += c.deltaPalets;
    }
    return [...map.values()];
  };

  const lineasActivasFinal = lineasExistentesEdit.filter(l => !l.eliminada).length + lineasNuevasEdit.filter(lineaCompleta).length;
  const formValidoEdit = !hayLineaExistenteInvalida && !hayLineaNuevaIncompleta && cambiosParaConfirmar().length > 0 && lineasActivasFinal > 0;

  const confirmarNuevoPedido = async () => {
    setGuardandoNuevo(true);
    try {
      const { data: pedido, error } = await supabase.from('pedidos').insert({
        almacen_id,
        numero_pedido: cabecera.numeroPedido.trim(),
        cliente: cabecera.cliente.trim() || null,
        fecha_pedido: cabecera.fechaPedido,
        fecha_solicitada: cabecera.fechaSolicitada || null,
        observaciones: cabecera.observaciones.trim() || null,
        usuario,
      }).select().single();
      if (error || !pedido) return;

      for (const l of lineasValidas) {
        const cajas = Number(l.cajas) || 0;
        const palets = Number(l.palets) || 0;
        const referencia = l.referencia.trim();

        await supabase.from('pedidos_lineas').insert({
          pedido_id: pedido.id,
          referencia,
          descripcion: l.descripcion.trim(),
          fecha_caducidad: l.fechaCaducidad,
          cantidad_cajas: cajas,
          cantidad_palets: palets,
        });
      }

      setConfirmandoNuevo(false);
      setVista("lista");
      await cargarPedidos();
      setExito("✅ Pedido creado correctamente");
      setTimeout(() => setExito(""), 2500);
    } finally {
      setGuardandoNuevo(false);
    }
  };

  const confirmarEdicionLineas = async () => {
    setGuardandoLineas(true);
    try {
      for (const c of cambiosParaConfirmar()) {
        if (c.accion === "delete") {
          await supabase.from('pedidos_lineas').delete().eq('id', c.lineaId);
        } else if (c.accion === "update") {
          await supabase.from('pedidos_lineas').update({ cantidad_cajas: c.nuevaCajas, cantidad_palets: c.nuevaPalets }).eq('id', c.lineaId);
        } else if (c.accion === "insert") {
          await supabase.from('pedidos_lineas').insert({
            pedido_id: pedidoActivo.id,
            referencia: c.referencia,
            descripcion: c.descripcion,
            fecha_caducidad: c.fecha_caducidad,
            cantidad_cajas: c.cajas,
            cantidad_palets: c.palets,
          });
        }
      }

      setConfirmandoLineas(false);
      setVista("detalle");
      await abrirDetalle(pedidoActivo);
      await cargarPedidos();
      setExito("✅ Líneas actualizadas correctamente");
      setTimeout(() => setExito(""), 2500);
    } finally {
      setGuardandoLineas(false);
    }
  };

  const transicionar = async (nuevoEstado) => {
    setGuardandoTransicion(true);
    try {
      await supabase.from('pedidos').update({ estado: nuevoEstado }).eq('id', pedidoActivo.id);
      setPedidoActivo(p => ({ ...p, estado: nuevoEstado }));
      await cargarPedidos();
    } finally {
      setGuardandoTransicion(false);
    }
  };

  const confirmarTerminar = async () => {
    if (!njInput.trim()) return;
    setGuardandoTransicion(true);
    setErrorTerminar("");
    try {
      const resultado = await terminarPedidoConDescuento({
        pedido: pedidoActivo,
        lineas: pedidoActivo.lineas,
        numero_albaran: pedidoActivo.numero_pedido,
        nj: njInput.trim(),
        usuario,
        almacen_id,
      });
      if (!resultado.ok) {
        setErrorTerminar(resultado.error);
        return;
      }
      setPedidoActivo(p => ({ ...p, estado: "terminado", nj: njInput.trim(), terminado_en: resultado.terminado_en }));
      setTerminando(false);
      await cargarPedidos();
    } finally {
      setGuardandoTransicion(false);
    }
  };

  const confirmarCancelacion = async () => {
    setGuardandoTransicion(true);
    try {
      await supabase.from('pedidos').update({ estado: "cancelado" }).eq('id', pedidoActivo.id);
      setPedidoActivo(p => ({ ...p, estado: "cancelado" }));
      setCancelando(false);
      await cargarPedidos();
    } finally {
      setGuardandoTransicion(false);
    }
  };

  const confirmarEliminarPedido = async () => {
    setGuardandoTransicion(true);
    try {
      const { data, error } = await supabase.from('pedidos').delete().eq('id', pedidoActivo.id).select();
      if (!error && data && data.length > 0) {
        setEliminando(false);
        setPedidoActivo(null);
        setVista("lista");
        await cargarPedidos();
      } else {
        window.alert("No se pudo eliminar el pedido. Supabase no confirmó el borrado (posible restricción de permisos). Contacta con soporte.");
      }
    } finally {
      setGuardandoTransicion(false);
    }
  };

  return (
    <div className="space-y-4">
      {vista === "lista" && (
        <>
          {exito && (
            <div className="bg-green-50 border-2 border-green-400 text-green-700 rounded-xl py-4 text-center">
              <span className="font-semibold">{exito}</span>
            </div>
          )}
          <div className="bg-white rounded-xl shadow p-4 space-y-3">
            <div className="flex items-center justify-between border-b pb-2 flex-wrap gap-2">
              <h2 className="font-bold text-gray-800">🧾 Preparación de pedidos</h2>
              <div className="flex gap-2">
                <button onClick={exportarPedidos} disabled={exportando || pedidosFiltrados.length === 0} className="bg-white text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-all">
                  {exportando ? "⏳ Generando..." : "⬇️ Exportar Excel"}
                </button>
                <button onClick={abrirNuevoPedido} className="bg-cyan-600 text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-cyan-700 transition-all">+ Nuevo pedido</button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {FILTROS_ESTADO.map(f => (
                <button
                  key={f.v}
                  onClick={() => setFiltroEstado(f.v)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg whitespace-nowrap transition-all ${filtroEstado === f.v ? "bg-cyan-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                >
                  {f.l}
                </button>
              ))}
            </div>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Buscar por nº pedido o cliente..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
            />
          </div>

          <div className="bg-white rounded-xl shadow overflow-x-auto">
            {cargando ? (
              <p className="text-sm text-gray-400 text-center py-6">Cargando pedidos...</p>
            ) : pedidosFiltrados.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Sin pedidos.</p>
            ) : (
              <table className="w-full text-sm min-w-max">
                <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Nº Pedido</th>
                    <th className="px-4 py-3 text-left">Cliente</th>
                    <th className="px-4 py-3 text-left">Fecha solicitada</th>
                    <th className="px-4 py-3 text-left">Estado</th>
                    <th className="px-4 py-3 text-left">NJ</th>
                  </tr>
                </thead>
                <tbody>
                  {pedidosFiltrados.map((p, idx) => {
                    const mostrarEncabezado = !filtroEstado && (idx === 0 || pedidosFiltrados[idx - 1].estado !== p.estado);
                    return (
                      <Fragment key={p.id}>
                        {mostrarEncabezado && (
                          <tr>
                            <td colSpan={5} className="px-4 py-1.5 bg-gray-50 text-[10px] font-bold text-gray-400 uppercase tracking-wide">{ESTADO_LABEL[p.estado]}</td>
                          </tr>
                        )}
                        <tr className="border-t hover:bg-gray-50 cursor-pointer" onClick={() => abrirDetalle(p)}>
                          <td className="px-4 py-2 font-semibold">
                            {p.numero_pedido}
                            {p.numero_pedido?.trim().toLowerCase() === "pendiente" && (
                              <span className="ml-2 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">⚠️ nº pendiente</span>
                            )}
                            {pedidosSinLineas.has(p.id) && (
                              <span className="ml-2 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full whitespace-nowrap">📦 sin líneas</span>
                            )}
                          </td>
                          <td className="px-4 py-2">{p.cliente || "—"}</td>
                          <td className="px-4 py-2">{formatFecha(p.fecha_solicitada)}</td>
                          <td className="px-4 py-2"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${ESTADO_STYLE[p.estado]}`}>{ESTADO_LABEL[p.estado]}</span></td>
                          <td className="px-4 py-2">{p.nj || "—"}</td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {vista === "nuevo" && (
        <>
          <button onClick={() => setVista("lista")} className="text-xs text-cyan-600 underline">← Volver a pedidos</button>
          <div className="bg-white rounded-xl shadow p-4 space-y-3">
            <h2 className="font-bold text-gray-800 border-b pb-2">🧾 Nuevo pedido</h2>
            <Inp label="Nº pedido *" value={cabecera.numeroPedido} onChange={v => setCabecera(c => ({ ...c, numeroPedido: v }))} placeholder="Nº de pedido" />
            <Inp label="Cliente" value={cabecera.cliente} onChange={v => setCabecera(c => ({ ...c, cliente: v }))} placeholder="Nombre del cliente" />
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Fecha pedido" type="date" value={cabecera.fechaPedido} onChange={v => setCabecera(c => ({ ...c, fechaPedido: v }))} />
              <Inp label="Fecha solicitada" type="date" value={cabecera.fechaSolicitada} onChange={v => setCabecera(c => ({ ...c, fechaSolicitada: v }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Observaciones</label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" rows={2} value={cabecera.observaciones} onChange={e => setCabecera(c => ({ ...c, observaciones: e.target.value }))} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 space-y-3">
            <h2 className="font-bold text-gray-800 border-b pb-2">📦 Líneas del pedido</h2>
            {lineas.map((l, idx) => {
              const superaCajas = (Number(l.cajas) || 0) > l.cajasDisponibles;
              const superaPalets = (Number(l.palets) || 0) > l.paletsDisponibles;
              return (
                <div key={idx} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 uppercase">Línea {idx + 1}</p>
                    {lineas.length > 1 && <button onClick={() => quitarLinea(idx)} className="text-xs text-red-500 underline">Quitar</button>}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase">Referencia</label>
                    <input
                      list="stock-referencias-pedido"
                      className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Referencia"
                      value={l.referencia}
                      onChange={e => actualizarLineaReferencia(idx, e.target.value)}
                    />
                  </div>
                  {l.referencia && l.lotes.length === 0 && (
                    <p className="text-xs text-red-600 font-semibold">⚠️ Sin stock disponible para esta referencia</p>
                  )}
                  {l.referencia && l.lotes.length > 0 && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">Lote (caducidad)</label>
                      <select
                        className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={l.loteId}
                        onChange={e => actualizarLineaLote(idx, e.target.value)}
                      >
                        <option value="">Selecciona un lote</option>
                        {l.lotes.map(lote => (
                          <option key={lote.id} value={lote.id}>{formatFecha(lote.fecha_caducidad)} — disponible: {lote.disponibleCajas} cajas, {lote.disponiblePalets} palets</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {l.loteId && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <Inp label={`Cajas (máx ${l.cajasDisponibles})`} type="number" value={l.cajas} onChange={v => actualizarLinea(idx, "cajas", v)} placeholder="0" />
                        <Inp label={`Palets (máx ${l.paletsDisponibles})`} type="number" value={l.palets} onChange={v => actualizarLinea(idx, "palets", v)} placeholder="0" />
                      </div>
                      {(superaCajas || superaPalets) && (
                        <p className="text-xs text-red-600 font-semibold">⚠️ Supera el stock disponible de este lote</p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            <datalist id="stock-referencias-pedido">{referenciasUnicas.map(s => <option key={s.referencia} value={s.referencia} />)}</datalist>
            <button onClick={añadirLinea} className="w-full border-2 border-dashed border-gray-300 rounded-xl py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all">
              + Añadir línea
            </button>
          </div>

          {hayLineaIncompleta && (
            <p className="text-xs text-red-600 font-semibold text-center">⚠️ Completa correctamente todas las líneas antes de continuar</p>
          )}

          <button
            onClick={() => setConfirmandoNuevo(true)}
            disabled={!formValido}
            className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all"
          >
            Revisar y confirmar →
          </button>
        </>
      )}

      {vista === "detalle" && pedidoActivo && (
        <>
          <button onClick={() => setVista("lista")} className="text-xs text-cyan-600 underline">← Volver a pedidos</button>
          <div className="bg-white rounded-xl shadow p-4 space-y-2">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="font-bold text-gray-800">Pedido {pedidoActivo.numero_pedido}</h2>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ESTADO_STYLE[pedidoActivo.estado]}`}>{ESTADO_LABEL[pedidoActivo.estado]}</span>
            </div>
            <p className="text-sm text-gray-600">Cliente: {pedidoActivo.cliente || "—"}</p>
            <p className="text-xs text-gray-500">Fecha pedido: {formatFecha(pedidoActivo.fecha_pedido)} · Fecha solicitada: {formatFecha(pedidoActivo.fecha_solicitada)}</p>
            {pedidoActivo.nj && <p className="text-xs text-gray-500">NJ: {pedidoActivo.nj}</p>}
            {pedidoActivo.observaciones && <p className="text-xs text-gray-500">Obs: {pedidoActivo.observaciones}</p>}
          </div>

          {ESTADOS_EDITABLES.includes(pedidoActivo.estado) && (
            <div className="flex gap-2">
              <button onClick={abrirEditarCabecera} className="flex-1 border border-cyan-300 text-cyan-700 font-semibold py-2 rounded-xl hover:bg-cyan-50 text-sm transition-all">✏️ Editar datos</button>
              <button onClick={abrirEditarLineas} className="flex-1 border border-cyan-300 text-cyan-700 font-semibold py-2 rounded-xl hover:bg-cyan-50 text-sm transition-all">📦 Editar líneas</button>
            </div>
          )}

          <div className="bg-white rounded-xl shadow overflow-x-auto">
            <table className="w-full text-sm min-w-max">
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Referencia</th>
                  <th className="px-4 py-3 text-left">Descripción</th>
                  <th className="px-4 py-3 text-left">Caduca</th>
                  <th className="px-4 py-3 text-right">Cajas</th>
                  <th className="px-4 py-3 text-right">Palets</th>
                </tr>
              </thead>
              <tbody>
                {pedidoActivo.lineas.map(l => (
                  <tr key={l.id} className="border-t">
                    <td className="px-4 py-2 font-semibold">{l.referencia}</td>
                    <td className="px-4 py-2">{l.descripcion}</td>
                    <td className="px-4 py-2">{formatFecha(l.fecha_caducidad)}</td>
                    <td className="px-4 py-2 text-right">{l.cantidad_cajas}</td>
                    <td className="px-4 py-2 text-right">{l.cantidad_palets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2">
            {pedidoActivo.estado === "pendiente" && (
              <button onClick={() => transicionar("aceptado")} disabled={guardandoTransicion} className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 disabled:opacity-40 transition-all">Aceptar</button>
            )}
            {pedidoActivo.estado === "aceptado" && (
              <button onClick={() => transicionar("en_proceso")} disabled={guardandoTransicion} className="flex-1 bg-amber-500 text-white font-bold py-3 rounded-xl hover:bg-amber-600 disabled:opacity-40 transition-all">Iniciar preparación</button>
            )}
            {pedidoActivo.estado === "en_proceso" && (
              <button onClick={() => { setNjInput(pedidoActivo.nj || ""); setErrorTerminar(""); setTerminando(true); }} disabled={guardandoTransicion} className="flex-1 bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 disabled:opacity-40 transition-all">Terminar</button>
            )}
            {pedidoActivo.estado !== "terminado" && pedidoActivo.estado !== "cancelado" && (
              <button onClick={() => setCancelando(true)} disabled={guardandoTransicion} className="flex-1 border border-red-300 text-red-600 font-bold py-3 rounded-xl hover:bg-red-50 disabled:opacity-40 transition-all">Cancelar</button>
            )}
            {pedidoActivo.estado === "cancelado" && (
              <button onClick={() => setEliminando(true)} disabled={guardandoTransicion} className="flex-1 bg-red-500 text-white font-bold py-3 rounded-xl hover:bg-red-600 disabled:opacity-40 transition-all">Eliminar</button>
            )}
          </div>
        </>
      )}

      {vista === "editar-lineas" && pedidoActivo && (
        <>
          <button onClick={() => setVista("detalle")} className="text-xs text-cyan-600 underline">← Volver al pedido</button>
          <div className="bg-white rounded-xl shadow p-4">
            <h2 className="font-bold text-gray-800 border-b pb-2">📦 Editar líneas — Pedido {pedidoActivo.numero_pedido}</h2>
          </div>

          <div className="bg-white rounded-xl shadow p-4 space-y-3">
            <h2 className="font-bold text-gray-800 border-b pb-2">Líneas actuales</h2>
            {lineasExistentesEdit.map((l, idx) => (
              <div key={l.id} className={`border rounded-lg p-3 space-y-2 ${l.eliminada ? "bg-red-50 opacity-60" : ""}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-gray-800">{l.referencia}{l.descripcion ? ` — ${l.descripcion}` : ""}</p>
                  <button onClick={() => toggleEliminarExistente(idx)} className={`text-xs underline ${l.eliminada ? "text-cyan-600" : "text-red-500"}`}>{l.eliminada ? "Deshacer" : "Quitar"}</button>
                </div>
                <p className="text-xs text-gray-400">Lote {formatFecha(l.fecha_caducidad)}</p>
                {!l.eliminada && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <Inp label={`Cajas (máx ${l.disponibleCajas + l.cantidadCajasOriginal})`} type="number" value={l.cajas} onChange={v => actualizarLineaExistente(idx, "cajas", v)} placeholder="0" />
                      <Inp label={`Palets (máx ${l.disponiblePalets + l.cantidadPaletsOriginal})`} type="number" value={l.palets} onChange={v => actualizarLineaExistente(idx, "palets", v)} placeholder="0" />
                    </div>
                    {lineaExistenteSupera(l) && (
                      <p className="text-xs text-red-600 font-semibold">⚠️ Supera el stock disponible de este lote</p>
                    )}
                    {lineaExistenteVacia(l) && (
                      <p className="text-xs text-red-600 font-semibold">⚠️ Usa "Quitar" para eliminar la línea, no dejes ambas cantidades a 0</p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-xl shadow p-4 space-y-3">
            <h2 className="font-bold text-gray-800 border-b pb-2">+ Añadir línea nueva</h2>
            {lineasNuevasEdit.map((l, idx) => {
              const superaCajas = (Number(l.cajas) || 0) > l.cajasDisponibles;
              const superaPalets = (Number(l.palets) || 0) > l.paletsDisponibles;
              return (
                <div key={idx} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 uppercase">Línea nueva {idx + 1}</p>
                    <button onClick={() => quitarLineaEdit(idx)} className="text-xs text-red-500 underline">Quitar</button>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 uppercase">Referencia</label>
                    <input
                      list="stock-referencias-pedido-edit"
                      className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                      placeholder="Referencia"
                      value={l.referencia}
                      onChange={e => actualizarLineaReferenciaEdit(idx, e.target.value)}
                    />
                  </div>
                  {l.referencia && l.lotes.length === 0 && (
                    <p className="text-xs text-red-600 font-semibold">⚠️ Sin stock disponible para esta referencia</p>
                  )}
                  {l.referencia && l.lotes.length > 0 && (
                    <div>
                      <label className="text-xs font-semibold text-gray-600 uppercase">Lote (caducidad)</label>
                      <select
                        className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        value={l.loteId}
                        onChange={e => actualizarLineaLoteEdit(idx, e.target.value)}
                      >
                        <option value="">Selecciona un lote</option>
                        {l.lotes.map(lote => (
                          <option key={lote.id} value={lote.id}>{formatFecha(lote.fecha_caducidad)} — disponible: {lote.disponibleCajas} cajas, {lote.disponiblePalets} palets</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {l.loteId && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <Inp label={`Cajas (máx ${l.cajasDisponibles})`} type="number" value={l.cajas} onChange={v => actualizarLineaEdit(idx, "cajas", v)} placeholder="0" />
                        <Inp label={`Palets (máx ${l.paletsDisponibles})`} type="number" value={l.palets} onChange={v => actualizarLineaEdit(idx, "palets", v)} placeholder="0" />
                      </div>
                      {(superaCajas || superaPalets) && (
                        <p className="text-xs text-red-600 font-semibold">⚠️ Supera el stock disponible de este lote</p>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            <datalist id="stock-referencias-pedido-edit">{referenciasUnicas.map(s => <option key={s.referencia} value={s.referencia} />)}</datalist>
            <button onClick={añadirLineaEdit} className="w-full border-2 border-dashed border-gray-300 rounded-xl py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all">
              + Añadir línea
            </button>
          </div>

          {hayLineaNuevaIncompleta && (
            <p className="text-xs text-red-600 font-semibold text-center">⚠️ Completa correctamente todas las líneas nuevas antes de continuar</p>
          )}
          {lineasActivasFinal === 0 && (
            <p className="text-xs text-red-600 font-semibold text-center">⚠️ El pedido debe tener al menos una línea</p>
          )}

          <button
            onClick={() => setConfirmandoLineas(true)}
            disabled={!formValidoEdit}
            className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all"
          >
            Revisar y confirmar →
          </button>
        </>
      )}

      {confirmandoNuevo && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">✅ Confirmar pedido</h2>
            {lineasValidas.length === 0 ? (
              <p className="text-xs text-amber-700 font-semibold bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">📦 Pedido sin líneas — se podrán añadir después desde Editar líneas</p>
            ) : (
              <>
                <p className="text-xs text-gray-500">Pedido {cabecera.numeroPedido} — esto se reservará del stock (el físico no cambia hasta terminar el pedido):</p>
                <div className="space-y-2">
                  {lineasValidas.map((l, i) => (
                    <div key={i} className="border rounded-lg p-3">
                      <p className="font-semibold text-gray-800 text-sm">{l.referencia}{l.descripcion ? ` — ${l.descripcion}` : ""}</p>
                      <p className="text-xs text-gray-600">Lote {formatFecha(l.fechaCaducidad)} · reserva: {Number(l.cajas) || 0} cajas · {Number(l.palets) || 0} palets</p>
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2">
              <button onClick={() => setConfirmandoNuevo(false)} disabled={guardandoNuevo} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">← Volver</button>
              <button onClick={confirmarNuevoPedido} disabled={guardandoNuevo} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardandoNuevo ? "⏳ Guardando..." : "Confirmar →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editandoCabecera && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">✏️ Editar datos del pedido</h2>
            <Inp label="Nº pedido *" value={cabeceraEdit.numeroPedido} onChange={v => setCabeceraEdit(c => ({ ...c, numeroPedido: v }))} placeholder="Nº de pedido" />
            <Inp label="Cliente" value={cabeceraEdit.cliente} onChange={v => setCabeceraEdit(c => ({ ...c, cliente: v }))} placeholder="Nombre del cliente" />
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Fecha pedido" type="date" value={cabeceraEdit.fechaPedido} onChange={v => setCabeceraEdit(c => ({ ...c, fechaPedido: v }))} />
              <Inp label="Fecha solicitada" type="date" value={cabeceraEdit.fechaSolicitada} onChange={v => setCabeceraEdit(c => ({ ...c, fechaSolicitada: v }))} />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Observaciones</label>
              <textarea className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400" rows={2} value={cabeceraEdit.observaciones} onChange={e => setCabeceraEdit(c => ({ ...c, observaciones: e.target.value }))} />
            </div>
            {!cabeceraEdit.numeroPedido.trim() && (
              <p className="text-xs text-red-600 font-semibold">⚠️ El nº de pedido es obligatorio</p>
            )}
            <div className="flex gap-2">
              <button onClick={() => setEditandoCabecera(false)} disabled={guardandoCabecera} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">Cancelar</button>
              <button onClick={guardarCabecera} disabled={guardandoCabecera || !cabeceraEdit.numeroPedido.trim()} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardandoCabecera ? "⏳ Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmandoLineas && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">✅ Confirmar cambios en líneas</h2>
            <p className="text-xs text-gray-500">Efecto en reservas por lote (el stock físico no cambia):</p>
            <div className="space-y-2">
              {resumenPorLote().map((r, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <p className="font-semibold text-gray-800 text-sm">{r.referencia} — Lote {formatFecha(r.fecha_caducidad)}</p>
                  {r.deltaCajas !== 0 && (
                    <p className={`text-xs font-semibold ${r.deltaCajas > 0 ? "text-green-600" : "text-red-600"}`}>
                      {r.deltaCajas > 0 ? `+${r.deltaCajas} cajas liberadas` : `${-r.deltaCajas} cajas más reservadas`}
                    </p>
                  )}
                  {r.deltaPalets !== 0 && (
                    <p className={`text-xs font-semibold ${r.deltaPalets > 0 ? "text-green-600" : "text-red-600"}`}>
                      {r.deltaPalets > 0 ? `+${r.deltaPalets} palets liberados` : `${-r.deltaPalets} palets más reservados`}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmandoLineas(false)} disabled={guardandoLineas} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">← Volver</button>
              <button onClick={confirmarEdicionLineas} disabled={guardandoLineas} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardandoLineas ? "⏳ Guardando..." : "Confirmar →"}
              </button>
            </div>
          </div>
        </div>
      )}

      {terminando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md space-y-4">
            <h3 className="font-bold text-gray-800 text-lg">✅ Terminar pedido</h3>
            <p className="text-xs text-gray-500">Al confirmar se descuenta el stock físico y se registra la salida.</p>
            <Inp label="NJ del albarán de salida *" value={njInput} onChange={setNjInput} placeholder="Nº de NJ" />
            {errorTerminar && (
              <p className="text-xs text-red-600 font-semibold bg-red-50 border border-red-200 rounded-lg px-3 py-2">⚠️ {errorTerminar}</p>
            )}
            <div className="flex gap-3 pt-2">
              <button onClick={() => { setTerminando(false); setErrorTerminar(""); }} disabled={guardandoTransicion} className="flex-1 bg-gray-100 text-gray-700 font-semibold py-2 rounded-xl hover:bg-gray-200 disabled:opacity-40 transition-all">Cancelar</button>
              <button onClick={confirmarTerminar} disabled={guardandoTransicion || !njInput.trim()} className="flex-1 bg-green-600 text-white font-semibold py-2 rounded-xl hover:bg-green-700 disabled:opacity-40 transition-all">
                {guardandoTransicion ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm text-center space-y-3">
            <p className="text-4xl">⚠️</p>
            <h3 className="font-bold text-gray-800 text-lg">¿Cancelar pedido?</h3>
            <p className="text-gray-500 text-sm">Se liberará la reserva de stock de este pedido (el stock físico no se ve afectado).</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setCancelando(false)} disabled={guardandoTransicion} className="flex-1 bg-gray-100 text-gray-700 font-bold py-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-all">Volver</button>
              <button onClick={confirmarCancelacion} disabled={guardandoTransicion} className="flex-1 bg-red-500 text-white font-bold py-2 rounded-lg hover:bg-red-600 disabled:opacity-40 transition-all">Sí, cancelar</button>
            </div>
          </div>
        </div>
      )}

      {eliminando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm text-center space-y-3">
            <p className="text-4xl">🗑️</p>
            <h3 className="font-bold text-gray-800 text-lg">¿Eliminar pedido?</h3>
            <p className="text-gray-500 text-sm">Esta acción es permanente y no se puede deshacer. El pedido {pedidoActivo?.numero_pedido} y sus líneas se eliminarán completamente.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setEliminando(false)} disabled={guardandoTransicion} className="flex-1 bg-gray-100 text-gray-700 font-bold py-2 rounded-lg hover:bg-gray-200 disabled:opacity-40 transition-all">Cancelar</button>
              <button onClick={confirmarEliminarPedido} disabled={guardandoTransicion} className="flex-1 bg-red-500 text-white font-bold py-2 rounded-lg hover:bg-red-600 disabled:opacity-40 transition-all">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
